// external
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import uniqid from "uniqid";

// components
import CustomButton from "components/partials/custom-button/custom-button";
import CustomSelect from "components/partials/custom-select/custom-select";
import StepNavigation from "components/StepNavigation";
import Loader from "components/partials/loader/loader";
import FaoBuilderHeader from "../fao-layout/fao-builder-header";
import DimensionEdit from "./dimension-edit";
import ProgressTracker from "components/ProgressTracker";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { FaoRegimeContext } from "context/FaoRegimeContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { ClientContext } from "context/ClientContext";

// data/constants
import { ATTRIBUTE_OPTIONS } from "../constants/attribute-options.constants";
import { CELL_METRICS } from "../constants/cell-metrics.constants";

// assets
import Icons from "assets/icons";

// css/styling
import { STYLES } from "../constants/styles.constants";

/*
TODO:
  - handle the logic of switching between different attributes better --> unit is weird
// */

const CellCheckbox = ({ id, label, checked, onChange }) => (
  <div className="flex items-center gap-2">
    <input
      key={id}
      id={id}
      className={STYLES.checkbox.input}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
    />
    <label htmlFor={id} className="font-family flex text-lg text-[#424242]">
      {label}
    </label>
  </div>
);

function FaoRegimeParams() {
  const navigate = useNavigate();
  const uniqueId = uniqid();
  const { currentClientData } = useContext(ClientContext);
  const { modelConfig, updateModelConfig } = useContext(FaoBuilderContext);

  const {
    selectedX,
    setSelectedX,
    selectedY,
    setSelectedY,
    dimensions_x,
    setDimensionsX,
    dimensions_y,
    setDimensionsY,
    cellMetrics,
    setCellMetrics,
  } = useContext(FaoRegimeContext);
  const [xPopupOpen, setXPopupOpen] = useState(false);
  const [yPopupOpen, setYPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [componentId, setComponentId] = useState(null);

  useEffect(() => {
    setIsSaved(false);
  }, [selectedX, selectedY, cellMetrics, dimensions_x, dimensions_y]);
  useEffect(() => {
    const handleLoad = async () => {
      const existingRegimeId = modelConfig.configs["regimeParams"];
      try {
        if (existingRegimeId !== "") {
          setComponentId(existingRegimeId);
          const res = await apiRoot.get(
            `/api/fao-regime/regimeparameters/${existingRegimeId}`
          );
          setDimensionsX(res.data.xDimensions);
          setDimensionsY(res.data.yDimensions);
          setSelectedX(res.data.xAxis);
          setSelectedY(res.data.yAxis);
          setCellMetrics(res.data.cellMetrics);
        } else {
          fetchNdrkDefaults("X");
          fetchAcrkDefaults("Y");
        }
      } catch (error) {
        console.log("Error loading regime data:", error);
      }
      setIsLoading(false);
    };
    handleLoad();
  }, []);

  async function fetchNdrkDefaults(dimension) {
    const toastMessages = {
      pending: `Fetching School Need Ranks`,
      success: `SUCCESS - School Need Ranks Found`,
      error: "FAILED - Please Reach out to Support for Assistance",
    };
    const promise = apiRoot.get(
      `/api/fao-regime/needrank/${currentClientData.schoolKey}`
    );
    const res = await toast.promise(promise, toastMessages);
    const formattedData = res.data.map((item) => [
      item.RangeStart,
      item.RangeEnd,
    ]);
    if (dimension === "X") {
      setDimensionsX(formattedData);
      setSelectedX("need");
    }
    if (dimension === "Y") {
      setDimensionsY(formattedData);
      setSelectedY("need");
    }
  }
  async function fetchAcrkDefaults(dimension) {
    const toastMessages = {
      pending: `Fetching School Need Ranks`,
      success: `SUCCESS - School Academic Ranks Found`,
      error: "FAILED - Please Reach out to Support for Assistance",
    };
    const promise = apiRoot.get(
      `/api/fao-regime/academicrank/${currentClientData.schoolKey}`
    );
    const res = await toast.promise(promise, toastMessages);
    const formattedData = res.data.map((item) => [
      item.AcademicRank,
      item.AcademicRank,
    ]);
    if (dimension === "X") {
      setDimensionsX(formattedData);
      setSelectedX("academicrank");
    }
    if (dimension === "Y") {
      setDimensionsY(formattedData);
      setSelectedY("academicrank");
    }
  }

  const renderDimensions = (dim, unit = "", selected) => {
    const type = ATTRIBUTE_OPTIONS.find((item) => item.value === selected).type;
    return type === "range" ? dim.map((val) => unit + val).join("-") : dim[0];
  };

  const handleSelectX = (selectedOption) => {
    setDimensionsX([]);
    setSelectedX(selectedOption.value);
    if (selectedOption.value === "need") {
      fetchNdrkDefaults("X");
    }
    if (selectedOption.value === "academicrank") {
      fetchAcrkDefaults("X");
    }
  };
  const handleSelectY = (selectedOption) => {
    setDimensionsY([]);
    setSelectedY(selectedOption.value);
    if (selectedOption.value === "need") {
      fetchNdrkDefaults("Y");
    }
    if (selectedOption.value === "academicrank") {
      fetchAcrkDefaults("Y");
    }
  };

  const handleCellMetricChange = (metric) => {
    console.log(metric);
    const updatedCellMetrics = Object.fromEntries(
      Object.entries(cellMetrics).map(([key, value]) => {
        // If the key matches the metric or both keys contain 'cap', toggle the value
        if (key === metric || (key.includes("cap") && metric.includes("cap"))) {
          return [key, !value];
        }
        return [key, value]; // No change for other keys
      })
    );
    setCellMetrics(updatedCellMetrics);
  };

  const renderAttributes = (label, dimension, selected, setPopupOpen) => {
    const unit =
      ATTRIBUTE_OPTIONS.find((option) => option.value === selected)?.unit || "";

    return (
      <div>
        <div className="flex flex-col items-start">
          <CustomSelect
            label={`Attribute ${label}`}
            id={`attribute${label}`}
            options={ATTRIBUTE_OPTIONS}
            value={selected}
            labelInTop={true}
            required={true}
            customClass={`${STYLES.label} mb-2`}
            customSelectClass="h-10 w-[550px]"
            onChange={label === "X" ? handleSelectX : handleSelectY}
            extraStyles={() => ({
              ...STYLES.select,
            })}
          />
        </div>
        <div>
          <div className="mt-5">
            <legend className={STYLES.label}>
              Attribute {label} Dimensions
            </legend>
          </div>
          <div className="flex items-center gap-2">
            {dimension.map((val, index) => (
              <div
                className="mt-2 rounded-md border border-solid border-gray-02 bg-white px-4 py-1 text-base leading-6 text-gray-04 no-underline shadow-bs-secondary"
                key={`${label}-dimension-${index}`}
              >
                {renderDimensions(val, unit, selected)}
              </div>
            ))}
            <CustomButton
              onClick={() => setPopupOpen(true)}
              variant="secondary"
              size="sm"
              customClass="mt-2 px-2 py-2"
              iconBefore={
                <Icons.Edit
                  aria-label="Edit Dimensions"
                  customClass="h-full w-4"
                />
              }
            ></CustomButton>
          </div>
        </div>
      </div>
    );
  };

  const renderCellChecks = () => {
    return (
      <div>
        <legend className={`${STYLES.label} flex`}>
          Cell Metrics{" "}
          <Icons.RequiredIndicator customClass="w-[7px] h-[7px] ml-1 mt-1" />
        </legend>
        <div className="mt-2 flex flex-row gap-5">
          {CELL_METRICS.filter((item) => item.isUserInput).map((item, _) => {
            return (
              <CellCheckbox
                id={item.value}
                label={`${item.label}, ${item?.unitAfter || item?.unitBefore} `}
                checked={cellMetrics[item.value]}
                onChange={() => handleCellMetricChange(item.value)}
              />
            );
          })}
        </div>
      </div>
    );
  };
  const postRegime = async () => {
    setIsSaving(true);
    try {
      const toastMessages = {
        pending: "Saving FAO Regime Parameters",
        success: "SUCCESS - FAO Regime Saved",
        error: "Regime not saved",
      };

      const importData = {
        schoolKey: currentClientData.schoolKey,
        xAxis: selectedX,
        yAxis: selectedY,
        xDimensions: dimensions_x,
        yDimensions: dimensions_y,
        cellMetrics: cellMetrics,
      };
      let promise;
      if (componentId) {
        promise = apiRoot.post(`/api/fao-regime/update-params/${componentId}`, {
          ...importData,
          id: componentId,
        });
        await toast.promise(promise, toastMessages);
      } else {
        setComponentId(uniqueId);
        promise = apiRoot.post(`/api/fao-regime/create-params`, {
          ...importData,
          id: uniqueId,
        });
        await toast.promise(promise, toastMessages);
        await updateModelConfig("regimeParams", uniqueId);
      }
    } catch (error) {
      console.log(error);
    }
    setIsSaving(false);
    setIsSaved(true);
  };

  const renderRegimeParams = () => {
    return (
      <div className="flex flex-col space-y-4">
        {renderAttributes("X", dimensions_x, selectedX, setXPopupOpen)}
        {xPopupOpen && (
          <DimensionEdit
            dimension={dimensions_x}
            setDimension={setDimensionsX}
            heading="Attribute X Dimensions"
            setPopupOpen={setXPopupOpen}
            type={
              ATTRIBUTE_OPTIONS.find((option) => option.value === selectedX)
                ?.type || ""
            }
          />
        )}
        {renderAttributes("Y", dimensions_y, selectedY, setYPopupOpen)}
        {yPopupOpen && (
          <DimensionEdit
            dimension={dimensions_y}
            setDimension={setDimensionsY}
            heading="Attribute Y Dimensions"
            setPopupOpen={setYPopupOpen}
            type={
              ATTRIBUTE_OPTIONS.find((option) => option.value === selectedY)
                ?.type || ""
            }
          />
        )}
        {renderCellChecks()}
      </div>
    );
  };
  return (
    <div>
      <ToastContainer
        style={{ width: "500px", fontSize: "16px" }}
        progressStyle={{ backgroundColor: "#b6de3a" }}
      />
      <FaoBuilderHeader
        modelName={modelConfig.versionName}
        modelId={modelConfig.id}
      />
      <ProgressTracker />
      {isLoading ? <Loader /> : renderRegimeParams()}
      <StepNavigation
        onClickBack={() => navigate(`../fao-builder/${modelConfig.id}/import`)}
        onClickSave={postRegime}
        disabledNext={
          dimensions_x.length === 0 || dimensions_y.length === 0 || !isSaved
        }
        onClickNext={() =>
          navigate(`../fao-builder/${modelConfig.id}/regime-config`)
        }
        isSaving={isSaving}
        isSaved={isSaved}
      ></StepNavigation>
    </div>
  );
}

export default FaoRegimeParams;
